import React from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';
import { Alert, Tooltip } from '@mui/material';

import {
  Container,
  Card,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
  PrivacyPolicyBlock,
} from '../../../../../../components/v2/Card';
import { ReactComponent as InstagramIcon } from '../../../../../../assets/icons/instagram.svg';
import { ReactComponent as FailedIcon } from '../../../../../../assets/icons/action_failed.svg';
import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/action_succeeded.svg';
import { PrimaryButton, AddButton } from '../../../../../../components/Button/ActionButtons';

import InstagramUser, { accountStatus, instagramPermissions } from '../../../../../../Model/InstagramUser';
import texts from './texts';
import { getInstagramAuthUrl } from './constants';
import {
  GrantedPermissionsContainer,
  GrantedPermissionItem,
  GrantedPermissionTitle,
  IconContainer,
  PermissionButtonContainer,
  ConnectOptionsRow,
  OrDivider,
} from './styles';

/**
 * Instagram Configuration component
 * @param {Object} props - Component props
 * @param {InstagramUser|null} props.instaUser - Instagram user data
 * @param {boolean} props.isSecondary - Whether this is the secondary outreach account
 * @param {InstagramUser|null} props.primaryInstaUser - Primary account, passed when isSecondary
 * @param {boolean} props.isSameAsPrimary - Whether the secondary is the same IG account as primary
 * @param {Function} props.onUsePrimary - Called when coach chooses to use primary as outreach account
 * @returns {JSX.Element} Instagram configuration card
 */
function InstagramConfiguration({
  instaUser, isSecondary, primaryInstaUser, isSameAsPrimary, onUsePrimary, coachId,
}) {
  const {
    accountStatus: connectedStatus = accountStatus.UNAUTHORIZED,
    grantedPermissions = [],
  } = instaUser || {};

  const isAuthorized = connectedStatus === accountStatus.AUTHORIZED;
  const title = isSecondary ? texts.outreachTitle : texts.title;
  const description = isSecondary ? texts.outreachDescription : texts.description;
  const connectLabel = isSecondary ? texts.connectDifferentAccount : texts.connectInstagramAccount;
  const primaryHasMessagePermission = primaryInstaUser?.grantedPermissions?.includes(
    instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES,
  );

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{title}</Title>
          <SubTitle>{description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        {isSecondary && !isAuthorized && primaryInstaUser ? (
          <ConnectOptionsRow>
            <PrimaryButton
              onClick={() => {
                window.location.href = getInstagramAuthUrl(undefined, isSecondary, coachId);
              }}
              $fitToContent
              icon={<InstagramIcon />}
            >
              {connectLabel}
            </PrimaryButton>
            <OrDivider>or</OrDivider>
            <Tooltip
              title={!primaryHasMessagePermission ? texts.useSameAccountDisabledTooltip : ''}
              placement="top"
            >
              <span>
                <PrimaryButton
                  $fitToContent
                  icon={<InstagramIcon />}
                  onClick={onUsePrimary}
                  disabled={!primaryHasMessagePermission}
                >
                  {texts.useSameAccount}
                </PrimaryButton>
              </span>
            </Tooltip>
          </ConnectOptionsRow>
        ) : (
          <>
            <PrimaryButton
              disabled={isAuthorized}
              onClick={() => {
                window.location.href = getInstagramAuthUrl(undefined, isSecondary, coachId);
              }}
              isLoading
              $fitToContent
              icon={<InstagramIcon />}
            >
              {isAuthorized
                ? format(texts.instagramAccountConnected, { handle: instaUser?.username })
                : connectLabel}
            </PrimaryButton>
            {isAuthorized && isSameAsPrimary && (
              <SubTitle>{texts.usingSameAccount}</SubTitle>
            )}
          </>
        )}
      </Container>
      {isAuthorized && (
        <GrantedPermissionsContainer>
          <Title>{texts.grantedPermissions}</Title>
          {Object.entries(texts.permissions).map(([key, permission]) => {
            const hasPermission = grantedPermissions?.includes(key);
            return (
              <GrantedPermissionItem key={key}>
                <IconContainer>
                  {hasPermission ? <CheckIcon /> : <FailedIcon />}
                </IconContainer>
                <GrantedPermissionTitle>
                  {permission.title}
                </GrantedPermissionTitle>
                {key === instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES
                  && !hasPermission
                  && (
                  <PermissionButtonContainer>
                    <AddButton
                      onClick={() => {
                        window.location.href = getInstagramAuthUrl(
                          instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES,
                          isSecondary,
                          coachId,
                        );
                      }}
                      $fitToContent
                    >
                      {texts.grantMessagePermission}
                    </AddButton>
                  </PermissionButtonContainer>
                  )}
              </GrantedPermissionItem>
            );
          })}
        </GrantedPermissionsContainer>
      )}

      {isSecondary && isAuthorized && !grantedPermissions?.includes(
        instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES,
      ) && (
        <Alert severity="warning">
          {texts.missingMessagePermissionsWarning}
        </Alert>
      )}

      {!isSecondary && (
        <PrivacyPolicyBlock>
          <a href="/instagram-privacy-policy" target="_blank">Privacy Policy</a>
        </PrivacyPolicyBlock>
      )}
    </Card>
  );
}

InstagramConfiguration.propTypes = {
  instaUser: PropTypes.instanceOf(InstagramUser),
  isSecondary: PropTypes.bool,
  primaryInstaUser: PropTypes.instanceOf(InstagramUser),
  isSameAsPrimary: PropTypes.bool,
  onUsePrimary: PropTypes.func,
  coachId: PropTypes.string,
};

InstagramConfiguration.defaultProps = {
  instaUser: undefined,
  isSecondary: false,
  primaryInstaUser: undefined,
  isSameAsPrimary: false,
  onUsePrimary: undefined,
  coachId: '',
};

export default InstagramConfiguration;
